/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */
import React from 'react';
import { jsx, cssMap } from '@compiled/react';
import { useIntl } from 'react-intl';

import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages/ai-suggestions';
import TeamworkGraphIcon from '@atlaskit/icon-lab/core/teamwork-graph';
import { Inline, Text } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import { Card } from '@atlaskit/smart-card/card/lazy';
import { SmartLinkSize, SmartLinkTheme } from '@atlaskit/smart-card/enums';
import { TitleBlock } from '@atlaskit/smart-card/flexible/title-block';
import { token } from '@atlaskit/tokens';

import type { Suggestion } from '@atlassian/editor-plugin-ai-suggestions';

import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary.ts';

import { getSourceApp, SourceAppIcon } from './suggestionSourceApps.tsx';

const cardUiOptions = {
	clickableContainer: true,
	hideBackground: true,
	hideElevation: true,
	hidePadding: true,
	size: SmartLinkSize.Medium,
	theme: SmartLinkTheme.Black,
} as const;

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = cssMap({
	sourcesContainer: {
		overflow: 'visible',
		width: '100%',
		minWidth: 0,
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.100'),
	},
	sourceLabel: {
		paddingInline: token('space.150'),
	},
	sourceCard: {
		position: 'relative',
		overflow: 'visible',
		width: '100%',
		boxSizing: 'border-box',
		minWidth: 0,
		minHeight: token('space.250'),
		paddingInline: token('space.150'),
	},
	/**
	 * The smart card is mounted immediately (so its IntersectionObserver fires
	 * and resolution begins) but kept invisible until onResolve fires, so the raw
	 * URL doesn't flash before it resolves.
	 */
	cardLoading: {
		visibility: 'hidden',
		// The initial lazy URL can wrap before intersection. Reserve one source row throughout loading.
		height: token('space.250'),
		overflow: 'hidden',
	},
	/**
	 * Skeleton overlay shown while the card resolves. Absolutely positioned so it
	 * sits on top of the (hidden) card without adding to the reserved height.
	 */
	skeletonOverlay: {
		position: 'absolute',
		inset: token('space.0'),
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		pointerEvents: 'none',
	},
});

// ---------------------------------------------------------------------------
// Skeleton shown while the smart card is resolving
// ---------------------------------------------------------------------------

function SourceSkeleton(): React.JSX.Element {
	return (
		<div css={styles.skeletonOverlay} aria-hidden>
			<Skeleton width="65%" height={12} borderRadius={token('radius.small')} isShimmering />
		</div>
	);
}

// ---------------------------------------------------------------------------
// SourceCard — renders the smart card with a skeleton overlay while loading
// ---------------------------------------------------------------------------

function SourceCard({ url }: { url: string }): React.JSX.Element | null {
	const [status, setStatus] = React.useState<'loading' | 'resolved' | 'errored'>('loading');
	const handleResolve = React.useCallback(() => setStatus('resolved'), []);
	const handleError = React.useCallback(() => setStatus('errored'), []);

	if (status === 'errored') {
		return null;
	}

	const app = getSourceApp(url);

	return (
		<React.Fragment>
			{/* Card is mounted hidden so its IntersectionObserver fires immediately;
			    it becomes visible once onResolve fires. */}
			<div css={[status === 'loading' && styles.cardLoading]}>
				<Card
					appearance="inline"
					ui={cardUiOptions}
					url={url}
					onResolve={handleResolve}
					onError={handleError}
				>
					<TitleBlock
						maxLines={1}
						anchorTarget="_blank"
						icon={app ? <SourceAppIcon app={app} size="xxsmall" /> : undefined}
					/>
				</Card>
			</div>
			{/* Skeleton sits alongside the hidden card while status === 'loading' */}
			{status === 'loading' && <SourceSkeleton />}
		</React.Fragment>
	);
}

export type SuggestionItemSourcesProps = {
	sources: NonNullable<Suggestion['sources']>;
};

function SuggestionItemSourcesInner({
	sources,
}: SuggestionItemSourcesProps): React.JSX.Element | null {
	const { formatMessage } = useIntl();

	// The backend supplies the URL, so a source without one can't be linked to and is
	// dropped — even when it has an ARI. De-duplicated so the same URL sent twice doesn't
	// render two identical cards.
	const urls = React.useMemo(() => {
		const unique = new Set<string>();

		for (const { url } of sources) {
			if (url) {
				unique.add(url);
			}
		}

		return Array.from(unique);
	}, [sources]);

	if (urls.length === 0) {
		return null;
	}

	return (
		<div css={styles.sourcesContainer}>
			<Inline xcss={styles.sourceLabel} space="space.100" alignBlock="center">
				<TeamworkGraphIcon label="" size="medium" color={token('color.icon.subtle')} />
				<Text as="span" color="color.text.subtle" size="medium" weight="medium">
					{formatMessage(aiSuggestionsMessages.teamworkGraphLabel)}
				</Text>
			</Inline>
			{urls.map((url) => (
				<div key={url} css={styles.sourceCard}>
					<SourceCard url={url} />
				</div>
			))}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Public export — wrapped in TransparentErrorBoundary so that unexpected
// errors (e.g. missing SmartCardProvider context in tests) render nothing
// rather than crashing the whole suggestions panel.
// ---------------------------------------------------------------------------

export function SuggestionItemSources({
	sources,
}: SuggestionItemSourcesProps): React.JSX.Element | null {
	if (!sources.length) {
		return null;
	}

	return (
		<TransparentErrorBoundary attribution="suggestions-panel">
			<SuggestionItemSourcesInner sources={sources} />
		</TransparentErrorBoundary>
	);
}
